import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, SkipForward, RotateCcw, CheckCircle2, HelpCircle, Award, Timer, Shield, ArrowLeft, Video } from "lucide-react";
import { api } from "../lib/api";

const GOLD = "#f59e0b";
const VARIANTS = [
  { key: "default", label: "Par défaut", field: null },
  { key: "no_equipment", label: "Sans équipement", field: "noEquipmentVariant" },
  { key: "bag", label: "Sac", field: "bagVariant" },
  { key: "pads_partner", label: "Paos/partenaire", field: "padsPartnerVariant" },
];
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function DemoVideos({ videos, compact = false }) {
  const [open, setOpen] = useState(compact ? null : videos[0]?.id);
  const cur = videos.find((v) => v.id === open);
  return (
    <div className={compact ? "mt-4" : ""} data-testid="session-demo-videos">
      <h3 className="text-sm font-bold text-amber-300 mb-2 flex items-center gap-1.5"><Video className="w-4 h-4" /> Vidéos démo ({videos.length})</h3>
      <div className="flex flex-wrap gap-2 mb-2">
        {videos.map((v) => (
          <button key={v.id} onClick={() => setOpen(open === v.id ? null : v.id)} data-testid={`demo-video-${v.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs border flex items-center gap-1.5 ${open === v.id ? "bg-amber-600 text-black border-amber-600" : "border-zinc-700 text-zinc-300 hover:border-amber-500/60"}`}>
            <Play className="w-3 h-3" /> {v.title}{v.duration_min ? <span className="opacity-70">· {v.duration_min} min</span> : null}
          </button>
        ))}
      </div>
      {cur && (
        <div className="aspect-video w-full rounded-xl overflow-hidden border border-zinc-800 bg-black" data-testid="demo-video-player">
          <iframe src={cur.embed_url} title={cur.title} className="w-full h-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen loading="lazy" />
        </div>
      )}
    </div>
  );
}

export default function MemberTrainingSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | running | done
  const [variant, setVariant] = useState("default");
  const [completionId, setCompletionId] = useState(null);
  const [blockIdx, setBlockIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [ticking, setTicking] = useState(false);
  const [doneBlocks, setDoneBlocks] = useState([]);
  const [checked, setChecked] = useState({});
  const [note, setNote] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/training/sessions/${id}`).then((r) => setS(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Séance indisponible."); nav(-1); });
  }, [id, nav]);

  const blocks = s?.sessionBlocks || [];
  const cur = blocks[blockIdx];

  const tick = useCallback(() => {
    setRemaining((r) => {
      if (r <= 1) { clearInterval(timerRef.current); setTicking(false); return 0; }
      return r - 1;
    });
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const start = async () => {
    try {
      const { data } = await api.post("/training/completions", { trainingSessionId: id, selectedVariant: variant });
      setCompletionId(data.id);
    } catch { /* still allow local practice */ }
    setPhase("running"); setBlockIdx(0);
    setRemaining((blocks[0]?.durationMinutes || 0) * 60);
  };
  const play = () => { if (ticking) return; setTicking(true); timerRef.current = setInterval(tick, 1000); };
  const pause = () => { clearInterval(timerRef.current); setTicking(false); };
  const reset = () => { pause(); setRemaining((cur?.durationMinutes || 0) * 60); };
  const next = async () => {
    pause();
    const nd = [...new Set([...doneBlocks, cur?.id])];
    setDoneBlocks(nd);
    if (completionId) api.patch(`/training/completions/${completionId}`, { completedBlockIds: nd }).catch(() => {});
    if (blockIdx + 1 < blocks.length) { setBlockIdx(blockIdx + 1); setRemaining((blocks[blockIdx + 1]?.durationMinutes || 0) * 60); }
    else setPhase("done");
  };
  const finish = async (status) => {
    if (completionId) {
      try {
        await api.patch(`/training/completions/${completionId}`, {
          status, reflectionAnswers: checked, memberNote: note || undefined,
        });
      } catch { /* noop */ }
    }
    toast.success(status === "needs_help" ? "On prévient ton instructeur 👊" : "Bravo, pratique enregistrée ! 🥋");
    nav(-1);
  };

  if (!s) return <div className="p-8 text-zinc-400">Chargement…</div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="member-training-session">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button onClick={() => nav(-1)} className="text-zinc-400 hover:text-white text-sm flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Retour</button>
        <div className="text-xs text-zinc-500 mb-1">Académie › {s.disciplineId} › {s.beltLevelId || "—"} › Entraînement</div>
        <h1 className="text-2xl sm:text-3xl font-black" style={{ color: GOLD }}>{s.title}</h1>
        <div className="flex items-center gap-3 text-sm text-zinc-400 mt-2 flex-wrap">
          <span className="flex items-center gap-1"><Timer className="w-4 h-4" /> {s.durationMinutes} min</span>
          <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-xs">{s.beltLevelId}</span>
          <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-xs">{s.accessLevel}</span>
          <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-xs capitalize">{s.estimatedIntensity}</span>
        </div>

        {phase === "intro" && (
          <div className="mt-5 space-y-4" data-testid="session-intro">
            {s.videos?.length > 0 && <DemoVideos videos={s.videos} />}
            {s.objectives?.length > 0 && (
              <div><h3 className="text-sm font-bold text-amber-300 mb-1">🎯 Objectifs</h3>
                <ul className="list-disc list-inside text-sm text-zinc-300 space-y-0.5">{s.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul></div>
            )}
            <div className="bg-amber-950/30 border border-amber-800/40 rounded-lg p-3 text-sm text-amber-200 flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 shrink-0" /> <span>{s.safetyNotice || "Entraîne-toi dans un espace libre et sécuritaire. Commence lentement, privilégie la technique et le contrôle."}</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-zinc-300 mb-2">Choisis ta variante</h3>
              <div className="flex flex-wrap gap-2">
                {VARIANTS.map((v) => {
                  const avail = v.field === null || !!s[v.field];
                  return (
                    <button key={v.key} disabled={!avail} onClick={() => setVariant(v.key)} data-testid={`variant-${v.key}`}
                      className={`px-3 py-2 rounded-lg text-sm border ${variant === v.key ? "bg-amber-600 text-black border-amber-600" : "border-zinc-700 text-zinc-300"} ${!avail ? "opacity-30 cursor-not-allowed" : ""}`}>
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <button onClick={start} data-testid="start-session-btn" className="w-full bg-amber-600 hover:bg-amber-500 text-black font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 text-lg">
              <Play className="w-5 h-5" /> Commencer l'entraînement
            </button>
          </div>
        )}

        {phase === "running" && cur && (
          <div className="mt-5" data-testid="session-running">
            <div className="flex gap-1.5 mb-4">
              {blocks.map((b, i) => <div key={b.id} className={`h-1.5 flex-1 rounded-full ${i < blockIdx ? "bg-green-500" : i === blockIdx ? "bg-amber-500" : "bg-zinc-700"}`} />)}
            </div>
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Bloc {blockIdx + 1}/{blocks.length}</div>
              <h2 className="text-xl font-bold mt-1">{cur.title}</h2>
              <div className="text-7xl font-black my-4 tabular-nums" style={{ color: ticking ? GOLD : "#e5e7eb" }} data-testid="timer-display">{fmt(remaining)}</div>
              {(cur.rounds || cur.roundDurationSeconds) && (
                <p className="text-sm text-zinc-400 mb-2">{cur.rounds ? `${cur.rounds} rounds` : ""}{cur.roundDurationSeconds ? ` · ${cur.roundDurationSeconds}s travail` : ""}{cur.restDurationSeconds ? ` · ${cur.restDurationSeconds}s repos` : ""}</p>
              )}
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4">
              <ul className="space-y-2 text-lg text-zinc-100">{(cur.instructions || []).map((ins, i) => <li key={i} className="flex gap-2"><span style={{ color: GOLD }}>›</span> {ins}</li>)}</ul>
            </div>
            <div className="flex gap-2">
              {!ticking ? <button onClick={play} data-testid="play-btn" className="flex-1 bg-green-700 hover:bg-green-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Play className="w-5 h-5" /> Démarrer</button>
                : <button onClick={pause} data-testid="pause-btn" className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Pause className="w-5 h-5" /> Pause</button>}
              <button onClick={reset} className="px-4 bg-zinc-800 rounded-xl" title="Recommencer le bloc"><RotateCcw className="w-5 h-5" /></button>
              <button onClick={next} data-testid="next-block-btn" className="px-5 bg-amber-600 hover:bg-amber-500 text-black font-bold rounded-xl flex items-center gap-1"><SkipForward className="w-5 h-5" /> Suivant</button>
            </div>
            {s.videos?.length > 0 ? <DemoVideos videos={s.videos} compact /> :
              s.linkedVideoIds?.length > 0 && <p className="text-center text-xs text-zinc-500 mt-3">💡 Revois la capsule technique dans ton carnet si besoin.</p>}
          </div>
        )}

        {phase === "done" && (
          <div className="mt-5" data-testid="session-selfeval">
            <div className="text-center mb-4"><Award className="w-12 h-12 mx-auto" style={{ color: GOLD }} /><h2 className="text-2xl font-black mt-2">Séance terminée !</h2><p className="text-zinc-400 text-sm">Fais ton auto-évaluation.</p></div>
            {s.successCriteria?.length > 0 && (
              <div className="mb-4"><h3 className="text-sm font-bold text-amber-300 mb-2">Critères de réussite</h3>
                {s.successCriteria.map((c, i) => (
                  <label key={i} className="flex items-start gap-2 text-sm text-zinc-200 mb-1.5">
                    <input type="checkbox" checked={!!checked[c]} onChange={(e) => setChecked({ ...checked, [c]: e.target.checked })} className="mt-0.5" /> {c}
                  </label>
                ))}
              </div>
            )}
            {s.studentReflectionQuestions?.length > 0 && (
              <div className="mb-4"><h3 className="text-sm font-bold text-amber-300 mb-2">Réflexion</h3>
                {s.studentReflectionQuestions.map((q, i) => (
                  <label key={i} className="flex items-start gap-2 text-sm text-zinc-200 mb-1.5">
                    <input type="checkbox" checked={!!checked[q]} onChange={(e) => setChecked({ ...checked, [q]: e.target.checked })} className="mt-0.5" /> {q}
                  </label>
                ))}
              </div>
            )}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Une note (optionnel)…" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm mb-3" data-testid="member-note" />
            <div className="grid gap-2">
              <button onClick={() => finish("completed")} data-testid="finish-completed" className="bg-green-700 hover:bg-green-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"><CheckCircle2 className="w-5 h-5" /> Pratique terminée</button>
              <button onClick={() => finish("needs_help")} data-testid="finish-help" className="bg-zinc-800 hover:bg-zinc-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"><HelpCircle className="w-5 h-5" /> J'ai besoin d'aide</button>
              <button onClick={() => finish("ready_for_review")} data-testid="finish-ready" className="bg-amber-600 hover:bg-amber-500 text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2"><Award className="w-5 h-5" /> Prêt à montrer à mon instructeur</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
